import { Payment } from '../../domain/entities/Payment';
import { PaymentRepository } from '../../domain/repositories/PaymentRepository';
import { v4 as uuidv4 } from 'uuid';

/**
 * Implementación mock del repositorio de pagos para desarrollo y pruebas
 */
export class MockPaymentRepository implements PaymentRepository {
  private payments: Payment[] = [];

  /**
   * Crea un nuevo registro de pago
   * @param payment - Datos del pago a crear
   * @returns Promesa que resuelve al pago creado
   */
  async create(payment: Omit<Payment, 'id'>): Promise<Payment> {
    // Simulamos un pequeño retraso para emular una petición de red
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newPayment: Payment = {
      id: uuidv4(),
      ...payment
    };
    
    this.payments.push(newPayment);
    return newPayment;
  }

  /**
   * Obtiene un pago por su ID
   * @param id - ID del pago a buscar
   * @returns Promesa que resuelve al pago encontrado, o null si no existe
   */
  async getById(id: string): Promise<Payment | null> {
    // Simulamos un pequeño retraso para emular una petición de red
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const payment = this.payments.find(p => p.id === id);
    return payment || null;
  }

  /**
   * Actualiza el estado de un pago
   * @param id - ID del pago a actualizar
   * @param status - Nuevo estado del pago
   * @returns Promesa que resuelve al pago actualizado
   */
  async updateStatus(id: string, status: Payment['status']): Promise<Payment> {
    // Simulamos un pequeño retraso para emular una petición de red
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const index = this.payments.findIndex(p => p.id === id);
    
    if (index === -1) {
      throw new Error(`Pago con ID ${id} no encontrado`);
    }
    
    this.payments[index] = {
      ...this.payments[index],
      status
    };
    
    return this.payments[index];
  }

  /**
   * Obtiene todos los pagos asociados a una factura
   * @param invoiceId - ID de la factura
   * @returns Promesa que resuelve a un array de pagos
   */
  async getByInvoiceId(invoiceId: string): Promise<Payment[]> {
    // Simulamos un pequeño retraso para emular una petición de red
    await new Promise(resolve => setTimeout(resolve, 350));
    
    return this.payments.filter(p => p.invoiceId === invoiceId);
  }

  /**
   * Consulta el estado actual de un pago por su referencia de transacción
   * @param transactionRef - Referencia de la transacción
   * @returns Promesa que resuelve al estado actual del pago
   */
  async checkPaymentStatus(transactionRef: string): Promise<{ ESTADO: 'Aprobada' | 'Rechazada' | 'Pendiente' }> {
    // Simulamos un pequeño retraso para emular una petición de red
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Para pruebas, retornamos un estado aleatorio
    const estados: Array<'Aprobada' | 'Rechazada' | 'Pendiente'> = ['Aprobada', 'Rechazada', 'Pendiente'];
    const estadoAleatorio = estados[Math.floor(Math.random() * estados.length)];
    
    return { ESTADO: estadoAleatorio };
  }

  /**
   * Registra un pago en el backend (mock)
   * @param paymentData - Datos del pago a registrar
   * @returns Promesa que resuelve con el ID del pago registrado
   */
  async registerPayment(paymentData: {
    uuid?: string;
    valorPagado: number;
    identificacion: string;
    estado: string;
    fechaPago: string;
    medioPago: string;
    factura: string;
    codigoFideicomiso?: string;
    oficina?: string;
    prefijo?: string;
    numeroFactura?: string;
    nombre?: string;
    detalleFactura?: string;
    fechaVencimiento?: string;
    intereses?: number;
    fechaFactura?: string;
    valorTotal?: number;
    secuencial?: number;
    valorSaldo?: number;
    id?: number;
  }): Promise<{ id: number }> {
    // Simulamos un pequeño retraso para emular una petición de red
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Si ya existe un ID, lo devolvemos (para actualización)
    if (paymentData.id) {
      console.log('Mock: Actualizando pago con ID:', paymentData.id, paymentData);
      return { id: paymentData.id };
    }
    
    // Generamos un ID numérico aleatorio para el mock
    const mockId = Math.floor(Math.random() * 1000000) + 1;
    console.log('Mock: Registrando pago con ID:', mockId, paymentData);
    
    return { id: mockId };
  }
} 